/**
 * `kind: 'pickParticipant'`. An `InputSpec` that can target the host or a player. One pick
 * sends a bare string and fires on tap; `count > 1` sends an array of exactly that length.
 */

import { useState } from 'react';
import type { InputSpec, ParticipantId } from '@gsf/contract';
import { ParticipantGrid } from '../../kit/ParticipantGrid';
import type { RendererProps } from '../renderer';

export function PickParticipantInput({
  request,
  spec,
  submit,
  nameOf,
  disabled,
}: RendererProps<Extract<InputSpec, { kind: 'pickParticipant' }>>) {
  const count = spec.count ?? 1;
  const [staged, setStaged] = useState<readonly ParticipantId[]>([]);

  // The echo, if offered: keeps the chosen name lit so a mis-tap can be fixed.
  const mine = request.mine?.value;
  const already: readonly ParticipantId[] =
    typeof mine === 'string'
      ? [mine as ParticipantId]
      : Array.isArray(mine)
        ? mine.filter((one): one is ParticipantId => typeof one === 'string')
        : [];

  const single = count === 1;

  return (
    <>
      {request.prompt ? <p className="play-hint">{request.prompt}</p> : null}
      <ParticipantGrid
        ids={spec.candidateIds}
        nameOf={nameOf}
        selectedIds={single ? already : staged}
        disabled={disabled}
        onPick={(id) => {
          if (single) {
            submit(id);
            return;
          }
          setStaged((current) =>
            current.includes(id)
              ? current.filter((one) => one !== id)
              : current.length < count
                ? [...current, id]
                : current,
          );
        }}
      />
      {single ? null : (
        <button
          className="btn btn-primary"
          type="button"
          disabled={disabled || staged.length !== count}
          onClick={() => submit([...staged])}
        >
          Confirm {staged.length} of {count}
        </button>
      )}
    </>
  );
}
